import {
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  InstrumentTypeResponse,
  OptionTypeResponse,
} from "../../codegen-api";
import { MarketContext } from "../../contexts/MarketContext";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import SegmentedControl from "../shared/SegmentedControl";

import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { WIDTHS } from "../../constants/design/spacing";
import { useOrder } from "../../hooks/api/order/useOrder";
import useScreenSize from "../../hooks/screenSize/useScreenSize";
import useTickersWSS from "../../hooks/wss/tickers/useTickersWSS";
import useIndexWSS from "../../hooks/wss/useIndexWSS";
import usePositionsWSS from "../../hooks/wss/usePositionsWSS";
import {
  getAssetFromSymbol,
  getContractPriceStep,
} from "../../utils/instruments";
import { Divider } from "../shared/Divider";
import CurrentSpotSection from "./CurrentSpotSection";
import DateSelection from "./DateSelection";
import {
  IOptionVolume,
  OptionsChainTableMode,
  TableContent,
  TableHeader,
} from "./OptionsTable";
import {
  OptionsChainContainer,
  OptionsTypeDateSelectionWrapper,
  OptionsTypeSelectWrapper,
  ScrollableContainer,
  ScrollableSection,
  TableContainer,
} from "./style";
import { IOptionsChainProps } from "./types";
import {
  computeFilteredRows,
  computeTotalCallsPutsVolume,
} from "./helpers";
import { useOptionsChainScrollJacking } from "./useOptionsChainScrollJacking";

export function OptionsChain({
  onTrade,
  activeInstrumentId,
}: IOptionsChainProps) {
  const strikeWidth: number = 112;
  const { market } = useContext(MarketContext);
  const { index: indexData } = useIndexWSS(market.asset);
  const currentIndexPrice = indexData?.price;
  const {
    selectedValues,
    expiries,
    setExpiry,
    setOrderType,
    selectedOptionInstrument,
    marketsData,
  } = useContext(MarketInstrumentContext);

  const { expiry } = selectedValues;
  const { positions, positionsMap } = usePositionsWSS();
  const { data: orders } = useOrder();

  const marketsOfThisExpiry = useMemo(
    () => marketsData?.filter((v) => Number(v.expiry) === expiry) || [],
    [expiry, marketsData]
  );

  const putCalls = useMemo(
    () => ({
      calls: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Call
      ),
      puts: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Put
      ),
    }),
    [marketsOfThisExpiry]
  );

  // Websocket
  const { instrumentTicker } = useTickersWSS(
    market.asset,
    InstrumentTypeResponse.Option,
    expiry
  );

  const scrollableRef = useRef<HTMLDivElement>(null);
  const currentSpotRef = useRef<HTMLDivElement>(null);
  const lowerThanSpotRowRefs = useRef<HTMLDivElement[]>([]);
  const higherThanSpotRowRefs = useRef<HTMLDivElement[]>([]);
  const { width } = useScreenSize();
  const { t } = useTranslation("app", {
    keyPrefix: "OptionsChain.OptionsChain",
  });

  // Used for filtering in screensize < isMediumScreen
  const defaultMobileOrderType = useMemo(
    () => selectedValues.orderType || "call",
    [selectedValues.orderType]
  );
  const optionsChainTableMode: OptionsChainTableMode = useMemo(
    () => (width < WIDTHS.M ? defaultMobileOrderType : "both"),
    [defaultMobileOrderType, width]
  );

  const decoratedExpiries = useMemo(() => {
    const withPositions =
      positions
        .filter((pos) => !!pos.option && pos.asset === market.asset)
        .map((pos) => pos.option!.expiry) ?? [];
    const withOrders =
      orders
        ?.filter(
          (o) =>
            !!o.expiry && getAssetFromSymbol(o.instrument_name) === market.asset
        )
        .map((o) => o.expiry!) ?? [];
    return {
      withPositions,
      withOrders,
    };
  }, [market.asset, orders, positions]);

  // Calculate the total volume for calls and puts
  const totalCallsPutsVolume: IOptionVolume = useMemo(
    () => computeTotalCallsPutsVolume(putCalls, instrumentTicker),
    [instrumentTicker, putCalls]
  );

  // Returns rows, split into below strike price and above strike price
  const filteredRows = useMemo(
    () =>
      computeFilteredRows(
        currentIndexPrice,
        putCalls,
        instrumentTicker,
        positionsMap,
        orders,
        expiry
      ),
    [
      putCalls,
      instrumentTicker,
      positionsMap,
      expiry,
      currentIndexPrice,
      orders,
    ]
  );

  useOptionsChainScrollJacking({
    expiry,
    selectedOptionInstrument,
    scrollableRef,
    currentSpotRef,
    lowerThanSpotRowRefs,
    higherThanSpotRowRefs,
    filteredRows,
    currentIndexPrice,
  });

  const onSelectExpiry = useCallback(
    (expiryDate: number) => {
      setExpiry(expiryDate);
    },
    [setExpiry]
  );

  const onSelectOptionTypeFilterMobile = useCallback(
    (value: string) => {
      setOrderType(value as OptionTypeResponse);
    },
    [setOrderType]
  );

  return (
    <OptionsChainContainer>
      <OptionsTypeDateSelectionWrapper>
        {optionsChainTableMode !== "both" && (
          <OptionsTypeSelectWrapper>
            <SegmentedControl
              segments={[
                {
                  value: String(OptionTypeResponse.Call),
                  display: t("calls"),
                  textColor:
                    optionsChainTableMode === OptionTypeResponse.Call
                      ? COLORS.blue.one
                      : TEXT_COLORS.three,
                },
                {
                  value: String(OptionTypeResponse.Put),
                  display: t("puts"),
                  textColor:
                    optionsChainTableMode === OptionTypeResponse.Put
                      ? COLORS.blue.one
                      : TEXT_COLORS.three,
                },
              ]}
              value={optionsChainTableMode}
              onSelect={onSelectOptionTypeFilterMobile}
              config={{
                theme: "outline",
                color: COLORS.blue.one,
                widthType: "fullWidth",
                backgroundColor: LAYER_COLORS.two,
                borderRadius: "10px",
                button: {
                  fontSize: FONT_SIZE.one,
                },
              }}
            />
            <Divider direction="vertical" size="100%" />
          </OptionsTypeSelectWrapper>
        )}
        <DateSelection
          expiries={expiries}
          selectedExpiry={expiry}
          expiriesWithPosition={decoratedExpiries.withPositions}
          expiriesWithOrder={decoratedExpiries.withOrders}
          onSelectExpiry={onSelectExpiry}
        />
      </OptionsTypeDateSelectionWrapper>
      <TableHeader
        strikeWidth={strikeWidth}
        expiry={expiry}
        mode={optionsChainTableMode}
      />
      <TableContainer>
        <ScrollableContainer>
          <ScrollableSection ref={scrollableRef}>
            <TableContent
              rows={filteredRows.lessThanCurrPriceRows}
              rowRefs={lowerThanSpotRowRefs}
              strikeWidth={strikeWidth}
              totalCallsPutsSize={totalCallsPutsVolume}
              onTrade={onTrade}
              activeInstrumentId={activeInstrumentId}
              mode={optionsChainTableMode}
              decimals={
                getContractPriceStep(selectedOptionInstrument).amount_precision
              }
            />
            <CurrentSpotSection
              ref={currentSpotRef}
              asset={market.asset}
              price={Number(currentIndexPrice)}
              loading={!currentIndexPrice}
            />
            <TableContent
              rows={filteredRows.moreThanCurrPriceRows}
              rowRefs={higherThanSpotRowRefs}
              strikeWidth={strikeWidth}
              totalCallsPutsSize={totalCallsPutsVolume}
              onTrade={onTrade}
              activeInstrumentId={activeInstrumentId}
              mode={optionsChainTableMode}
              decimals={
                getContractPriceStep(selectedOptionInstrument).amount_precision
              }
            />
          </ScrollableSection>
        </ScrollableContainer>
      </TableContainer>
    </OptionsChainContainer>
  );
}
